import { useEffect, useState, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {toast } from "react-toastify";
const API = process.env.REACT_APP_API_URL || "https://doctor-appointment-1-yf2j.onrender.com";

/* ─── Particle System Component ─────────────────────────────────────────── */
const FloatingParticles = () => {
  const canvasRef = useRef(null);
  const particles = useRef([]);
  const mouse = useRef({ x: 0, y: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext("2d");
    let animationFrameId;

    const handleResize = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };

    const createParticles = () => {
      const particleCount = 120;
      particles.current = [];
      for (let i = 0; i < particleCount; i++) {
        particles.current.push({
          x: Math.random() * canvas.width,
          y: Math.random() * canvas.height,
          size: Math.random() * (i % 5 === 0 ? 12 : 4) + 1, // some larger "flowers" (circles/dots)
          speedX: (Math.random() - 0.5) * 0.8,
          speedY: (Math.random() - 0.5) * 0.8,
          color: i % 5 === 0 ? "rgba(99, 179, 237, 0.4)" : "rgba(190, 227, 248, 0.6)",
          type: i % 10 === 0 ? "flower" : "dot"
        });
      }
    };

    const handleMouseMove = (e) => {
      mouse.current.x = e.clientX;
      mouse.current.y = e.clientY;
    };

    const drawFlowerSymbol = (ctx, x, y, size, color) => {
      ctx.beginPath();
      ctx.fillStyle = color;
      for (let i = 0; i < 5; i++) {
        const angle = (i * 2 * Math.PI) / 5;
        const px = x + Math.cos(angle) * (size / 2);
        const py = y + Math.sin(angle) * (size / 2);
        ctx.moveTo(px, py);
        ctx.arc(px, py, size / 3, 0, Math.PI * 2);
      }
      ctx.fill();
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      particles.current.forEach((p) => {
        p.x += p.speedX;
        p.y += p.speedY;

        // Bounce
        if (p.x < 0 || p.x > canvas.width) p.speedX *= -1;
        if (p.y < 0 || p.y > canvas.height) p.speedY *= -1;

        // Mouse interaction (move away)
        const dx = p.x - mouse.current.x;
        const dy = p.y - mouse.current.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 150) {
          p.x += dx * 0.03;
          p.y += dy * 0.03;
        }

        if (p.type === "flower") {
          drawFlowerSymbol(ctx, p.x, p.y, p.size, p.color);
        } else {
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fillStyle = p.color;
          ctx.fill();
        }
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    window.addEventListener("resize", handleResize);
    window.addEventListener("mousemove", handleMouseMove);
    handleResize();
    createParticles();
    animate();

    return () => {
      window.removeEventListener("resize", handleResize);
      window.removeEventListener("mousemove", handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return <canvas ref={canvasRef} style={{ position: "fixed", inset: 0, zIndex: 0, pointerEvents: "none" }} />;
};

/* ─── DoctorsList Component ────────────────────────────────────────────── */
const STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800&display=swap');

  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

  .dl-root {
    min-height: 100vh;
    background: linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%);
    font-family: 'Outfit', sans-serif;
    color: #1e293b;
    position: relative;
    overflow-x: hidden;
  }

  /* ── Hero ── */
  .dl-hero {
    position: relative;
    padding: clamp(80px, 12vw, 120px) 24px 110px;
    text-align: center;
    background: transparent;
    z-index: 1;
  }
  .dl-hero-eyebrow {
    display: inline-block;
    color: #0369a1;
    font-size: 12px;
    font-weight: 700;
    letter-spacing: 4px;
    text-transform: uppercase;
    margin-bottom: 20px;
    background: rgba(3, 105, 161, 0.08);
    padding: 8px 20px;
    border-radius: 50px;
    border: 1px solid rgba(3, 105, 161, 0.15);
  }
  .dl-hero h1 {
    font-size: clamp(36px, 6vw, 68px);
    font-weight: 800;
    color: #0c4a6e;
    line-height: 1.1;
    letter-spacing: -2px;
    margin-bottom: 18px;
  }
  .dl-hero h1 span {
    background: linear-gradient(90deg, #0284c7, #7c3aed);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
  .dl-hero-sub {
    font-size: clamp(16px, 2.2vw, 19px);
    color: #475569;
    margin-bottom: 48px;
    max-width: 520px;
    margin-left: auto;
    margin-right: auto;
    line-height: 1.6;
  }

  /* ── Search ── */
  .dl-search-wrap {
    display: flex;
    justify-content: center;
    position: relative;
    z-index: 2;
  }
  .dl-search-inner {
    position: relative;
    width: 100%;
    max-width: 580px;
  }
  .dl-search-icon {
    position: absolute;
    left: 20px;
    top: 50%;
    transform: translateY(-50%);
    color: #0284c7;
    pointer-events: none;
  }
  .dl-search-input {
    width: 100%;
    padding: 20px 24px 20px 60px;
    border-radius: 60px;
    border: 2px solid #bae6fd;
    font-size: 16px;
    font-family: 'Outfit', sans-serif;
    background: rgba(255, 255, 255, 0.9);
    color: #0c4a6e;
    backdrop-filter: blur(20px);
    outline: none;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 12px 40px rgba(186, 230, 253, 0.4);
  }
  .dl-search-input::placeholder { color: #94a3b8; }
  .dl-search-input:focus {
    border-color: #0284c7;
    box-shadow: 0 16px 50px rgba(186, 230, 253, 0.6);
    transform: scale(1.02);
  }

  /* ── Stats bar ── */
  .dl-stats {
    background: rgba(255, 255, 255, 0.4);
    border-top: 1px solid rgba(186, 230, 253, 0.3);
    border-bottom: 1px solid rgba(186, 230, 253, 0.3);
    padding: 16px 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #64748b;
    z-index: 1;
    position: relative;
  }
  .dl-stats strong { color: #0284c7; font-weight: 700; }

  /* ── Grid ── */
  .dl-grid {
    max-width: 1450px;
    margin: 0 auto;
    padding: 60px 24px 100px;
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
    gap: 24px;
    position: relative;
    z-index: 1;
  }

  /* ── Card ── */
  .dl-card-outer {
    position: relative;
    border-radius: 24px;
    padding: 2px;
    background: white;
    cursor: pointer;
    transition: all 0.4s cubic-bezier(.22,.68,0,1.2);
    box-shadow: 0 10px 30px rgba(186, 230, 253, 0.2);
  }
  .dl-card-outer:hover {
    transform: translateY(-10px) scale(1.02);
    box-shadow: 0 30px 60px rgba(186, 230, 253, 0.5);
  }

  /* Ray-of-light ring (adjusted for light theme) */
  .dl-card-ring {
    position: absolute;
    inset: 0;
    border-radius: 24px;
    padding: 2px;
    background: conic-gradient(
      from var(--angle, 0deg),
      transparent 0deg,
      #0284c7 60deg,
      #7c3aed 120deg,
      #0ea5e9 180deg,
      transparent 240deg
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    opacity: 0;
    transition: opacity 0.4s ease;
    pointer-events: none;
  }
  .dl-card-outer:hover .dl-card-ring {
    opacity: 1;
    animation: spin-light 2s linear infinite;
  }
  @property --angle {
    syntax: '<angle>';
    initial-value: 0deg;
    inherits: false;
  }
  @keyframes spin-light {
    to { --angle: 360deg; }
  }

  .dl-card-inner {
    background: #ffffff;
    border-radius: 22px;
    overflow: hidden;
    display: flex;
    flex-direction: column;
    height: 100%;
    border: 1px solid #f0f9ff;
  }

  /* Image */
  .dl-card-img-wrap {
    position: relative;
    overflow: hidden;
    height: 160px;
    flex-shrink: 0;
  }
  .dl-card-img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.6s ease;
    display: block;
  }
  .dl-card-outer:hover .dl-card-img { transform: scale(1.1); }

  .dl-badge {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 11px;
    font-weight: 800;
    padding: 6px 14px;
    border-radius: 50px;
    backdrop-filter: blur(12px);
    display: flex;
    align-items: center;
    gap: 6px;
    box-shadow: 0 4px 12px rgba(0,0,0,0.1);
  }
  .dl-badge-active {
    background: rgba(220, 252, 231, 0.9);
    color: #16a34a;
    border: 1px solid #bbf7d0;
  }
  .dl-badge-inactive {
    background: rgba(254, 226, 226, 0.9);
    color: #dc2626;
    border: 1px solid #fecaca;
  }

  /* Card body */
  .dl-card-body {
    padding: 20px;
    flex: 1;
    display: flex;
    flex-direction: column;
  }
  .dl-card-name {
    font-size: 18px;
    font-weight: 800;
    color: #0c4a6e;
    margin-bottom: 2px;
  }
  .dl-card-spec {
    font-size: 13px;
    font-weight: 700;
    color: #0284c7;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-bottom: 6px;
  }
  .dl-card-exp {
    font-size: 12px;
    color: #64748b;
    margin-bottom: 12px;
  }

  .dl-divider {
    height: 1px;
    background: #f1f5f9;
    margin-bottom: 12px;
  }

  .dl-card-meta {
    display: flex;
    flex-direction: column;
    gap: 6px;
    margin-bottom: 16px;
  }
  .dl-card-meta-row {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
    color: #475569;
  }
  .dl-card-meta-icon {
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: #f0f9ff;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 12px;
    border: 1px solid #e0f2fe;
  }

  .dl-card-fee {
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: #f0f9ff;
    border: 1px solid #bae6fd;
    border-radius: 12px;
    padding: 8px 14px;
    margin-bottom: 16px;
  }
  .dl-card-fee-label { font-size: 11px; color: #64748b; font-weight: 500; }
  .dl-card-fee-value { font-size: 18px; font-weight: 800; color: #0369a1; }

  .dl-card-btn {
    width: 100%;
    padding: 10px;
    border-radius: 12px;
    border: none;
    background: linear-gradient(135deg, #0284c7, #0ea5e9);
    color: #fff;
    font-size: 14px;
    font-weight: 700;
    font-family: 'Outfit', sans-serif;
    cursor: pointer;
    transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
    margin-top: auto;
    box-shadow: 0 4px 15px rgba(2, 132, 199, 0.3);
  }
  .dl-card-btn:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(2, 132, 199, 0.5);
    background: linear-gradient(135deg, #0ea5e9, #0284c7);
  }

  .dl-empty {
    text-align: center;
    padding: 120px 24px;
    position: relative;
    z-index: 1;
  }

  /* ── Filters ── */
  .dl-category-toggles {
    display: flex;
    justify-content: center;
    gap: 12px;
    margin-top: 24px;
    position: relative;
    z-index: 2;
  }
  .dl-cat-btn {
    padding: 10px 24px;
    border-radius: 40px;
    border: 2px solid #bae6fd;
    background: rgba(255, 255, 255, 0.8);
    color: #0c4a6e;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.3s ease;
    backdrop-filter: blur(10px);
  }
  .dl-cat-btn:hover {
    background: #e0f2fe;
  }
  .dl-cat-btn.active {
    background: #0284c7;
    color: white;
    border-color: #0284c7;
    box-shadow: 0 4px 15px rgba(2, 132, 199, 0.3);
  }

  .dl-advanced-filters {
    display: flex;
    justify-content: center;
    gap: 16px;
    margin-top: 20px;
    position: relative;
    z-index: 2;
    flex-wrap: wrap;
    animation: fadeInDown 0.4s ease forwards;
  }
  @keyframes fadeInDown {
    from { opacity: 0; transform: translateY(-10px); }
    to { opacity: 1; transform: translateY(0); }
  }
  .dl-filter-select, .dl-filter-input {
    padding: 12px 18px;
    border-radius: 12px;
    border: 1px solid #bae6fd;
    background: rgba(255, 255, 255, 0.9);
    color: #0c4a6e;
    font-size: 14px;
    font-family: 'Outfit', sans-serif;
    outline: none;
    transition: all 0.3s ease;
    min-width: 160px;
  }
  .dl-filter-select:focus, .dl-filter-input:focus {
    border-color: #0284c7;
    box-shadow: 0 4px 15px rgba(186, 230, 253, 0.5);
  }
`;

const DoctorsList = () => {
  const [doctors, setDoctors] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [selectedSpecialization, setSelectedSpecialization] = useState("");
  const [selectedCity, setSelectedCity] = useState("");
  const [maxFee, setMaxFee] = useState("");
  const navigate = useNavigate();
  const styleInjected = useRef(false);

  useEffect(() => {
    if (!styleInjected.current) {
      const tag = document.createElement("style");
      tag.textContent = STYLES;
      document.head.appendChild(tag);
      styleInjected.current = true;
    }
    fetchDoctors();
  }, []);

  const fetchDoctors = async () => {
    try {
      const res = await axios.get(`${API}/api/doctors/all`);
      setDoctors(res.data);
    } catch (e) {
      console.error(e);
    }
  };

  const filteredByCategory = doctors.filter(doc => selectedCategory === "All" || (doc.category || "Doctor") === selectedCategory);
  
  const uniqueSpecializations = [...new Set(filteredByCategory.map(d => d.specialization).filter(Boolean))];
  const uniqueCities = [...new Set(filteredByCategory.map(d => d.city).filter(Boolean))];

  const filteredDoctors = doctors.filter((doc) => {
    const docCategory = doc.category || "Doctor";
    if (selectedCategory !== "All" && docCategory !== selectedCategory) return false;
    if (selectedSpecialization && doc.specialization !== selectedSpecialization) return false;
    if (selectedCity && doc.city !== selectedCity) return false;
    if (maxFee && doc.consultation_fee != null && doc.consultation_fee > parseInt(maxFee)) return false;

    if (search) {
      const s = search.toLowerCase();
      if (![doc.name, doc.specialization, doc.clinic_name, doc.city, doc.area, doc.pincode].some(val => val?.toLowerCase().includes(s))) {
        return false;
      }
    }
    return true;
  }).sort((a, b) => {
    try {
      const user = JSON.parse(localStorage.getItem("user") || "null");
      const userPin = (user?.pincode || "").trim();
      const userCity = (user?.city || "").trim().toLowerCase();
      if (userPin) {
        const pinA = (a.pincode || "").trim();
        const pinB = (b.pincode || "").trim();
        const matchA = pinA && pinA.slice(0, 3) === userPin.slice(0, 3) ? 1 : 0;
        const matchB = pinB && pinB.slice(0, 3) === userPin.slice(0, 3) ? 1 : 0;
        if (matchA !== matchB) return matchB - matchA;
        const diffA = pinA ? Math.abs(parseInt(pinA || 0) - parseInt(userPin || 0)) : 999999;
        const diffB = pinB ? Math.abs(parseInt(pinB || 0) - parseInt(userPin || 0)) : 999999;
        return diffA - diffB;
      }
      if (userCity) {
        const cityA = (a.city || "").trim().toLowerCase() === userCity ? 1 : 0;
        const cityB = (b.city || "").trim().toLowerCase() === userCity ? 1 : 0;
        return cityB - cityA;
      }
    } catch {}
    return 0;
  });

  return (
    <div className="dl-root">
      <FloatingParticles />
         {/* Hero */}
      <div className="dl-hero">
        <span className="dl-hero-eyebrow">Healthcare Directory</span>
        <h1>Find Your <span>Doctor</span></h1>
        <p className="dl-hero-sub">Connect with trusted specialists near you. Move your mouse to see the background react!</p>

        <div className="dl-search-wrap">
          <div className="dl-search-inner">
            <svg className="dl-search-icon" width="22" height="22" fill="none"
              stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            <input
              className="dl-search-input"
              type="text"
              placeholder="Search by name, clinic, or city…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </div>

        <div className="dl-category-toggles">
          {["All", "Doctor", "Therapist"].map(cat => (
            <button 
              key={cat} 
              className={`dl-cat-btn ${selectedCategory === cat ? 'active' : ''}`}
              onClick={() => {
                setSelectedCategory(cat);
                setSelectedSpecialization("");
                setMaxFee("");
                setSelectedCity("");
              }}
            >
              {cat}
            </button>
          ))}
        </div>
        
        {selectedCategory !== "All" && (
          <div className="dl-advanced-filters">
            <select value={selectedSpecialization} onChange={(e) => setSelectedSpecialization(e.target.value)} className="dl-filter-select">
              <option value="">All Specializations</option>
              {uniqueSpecializations.map(spec => (
                <option key={spec} value={spec}>{spec}</option>
              ))}
            </select>
            
            <select value={selectedCity} onChange={(e) => setSelectedCity(e.target.value)} className="dl-filter-select">
              <option value="">All Cities</option>
              {uniqueCities.map(city => (
                <option key={city} value={city}>{city}</option>
              ))}
            </select>

            <input 
              type="number" 
              placeholder="Max Price (₹)" 
              value={maxFee} 
              onChange={(e) => setMaxFee(e.target.value)}
              className="dl-filter-input"
            />
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="dl-stats">
        <strong>{filteredDoctors.length}</strong>
        {search ? ` specialists match "${search}"` : " specialists found in your area"}
      </div>

      {/* Grid */}
      {filteredDoctors.length > 0 ? (
        <div className="dl-grid">
          {filteredDoctors.map((doc) => (
            <DoctorCard key={doc.id} doc={doc} navigate={navigate} />
          ))}
        </div>
      ) : (
        <div className="dl-empty">
          <div style={{ fontSize: 70, marginBottom: 20 }}>🌿</div>
          <h3 style={{ fontSize: 24, fontWeight: 700, color: "#0c4a6e" }}>No matching doctors</h3>
          <p style={{ color: "#64748b" }}>We couldn't find any results for "{search}".</p>
        </div>
      )}
    </div>
  );
};


/* ─── Card ──────────────────────────────────────────────────────────────────── */
const DoctorCard = ({ doc, navigate }) => {
  const handleClick = () => {
    navigate(`/doctor/${doc.id}`);
  };

  return (
    <div className="dl-card-outer" onClick={handleClick}>
      <div className="dl-card-ring" />
      <div className="dl-card-inner">
        <div className="dl-card-img-wrap">
          <img
            className="dl-card-img"
            src={
              doc.profile_image
                ? `${API}/uploads/doctors/${doc.profile_image}`
                : `https://ui-avatars.com/api/?name=${encodeURIComponent(doc.name || "Doctor")}&background=bae6fd&color=0369a1&size=400`
            }
            alt={doc.name}
            onError={(e) => {
              e.target.src = `https://ui-avatars.com/api/?name=${encodeURIComponent(doc.name || "Doctor")}&background=bae6fd&color=0369a1&size=400`;
            }}
          />
          <span className={`dl-badge ${doc.is_active ? "dl-badge-active" : "dl-badge-inactive"}`}>
            {doc.is_active ? "Available" : "Unavailable"}
          </span>
        </div>

        <div className="dl-card-body">
          <h2 className="dl-card-name">{doc.name || "Dr. Anonymous"}</h2>
          <p className="dl-card-spec">{doc.specialization}</p>
          <p className="dl-card-exp">
            {doc.experience ? `${doc.experience} yrs exp.` : "Experienced Professional"}
          </p>

          <div className="dl-divider" />

          <div className="dl-card-meta">
            <div className="dl-card-meta-row">
              <span className="dl-card-meta-icon">🆔</span>
              <span>ID: D-{String(doc.id).padStart(6, '0')}</span>
            </div>
            {doc.clinic_name && (
              <div className="dl-card-meta-row">
                <span className="dl-card-meta-icon">🏢</span>
                <span>{doc.clinic_name}</span>
              </div>
            )}
            {(doc.city && doc.city !== "null" && doc.city !== "undefined") && (
              <div className="dl-card-meta-row">
                <span className="dl-card-meta-icon">📍</span>
                <span>{doc.area && doc.area !== "null" && doc.area !== "undefined" ? doc.area : doc.city} {doc.pincode && doc.pincode !== "null" && doc.pincode !== "undefined" ? `(${doc.pincode})` : ""}</span>
              </div>
            )}
            {doc.availability_start && (
              <div className="dl-card-meta-row">
                <span className="dl-card-meta-icon">⏰</span>
                <span>
                  {(() => {
                    const formatAMPM = (timeStr) => {
                      if (!timeStr) return "";
                      const [h, m] = timeStr.split(':');
                      const hours = parseInt(h, 10);
                      const period = hours >= 12 ? 'PM' : 'AM';
                      const formattedH = hours % 12 || 12;
                      return `${formattedH}:${m} ${period}`;
                    };
                    return `${formatAMPM(doc.availability_start)} - ${formatAMPM(doc.availability_end)}`;
                  })()}
                </span>
              </div>
            )}
          </div>

          <div className="dl-card-fee">
            <span className="dl-card-fee-label">Consultation</span>
            <span className="dl-card-fee-value">₹{doc.consultation_fee ?? "—"}</span>
          </div>

          <button className="dl-card-btn">
            View Profile
          </button>
        </div>
      </div>
    </div>
  );
};


export default DoctorsList;